import { AbstractControl, FormGroup } from "@angular/forms";
import { EmptyInstance } from "core-library/mixins/empty-instance";
import _cloneDeep from "lodash/cloneDeep";
import { distinctUntilChanged, Observable, Subject, takeUntil } from "rxjs";

import { DestroyableMixin } from "../mixins/destroyable.mixin";

export abstract class BaseObservableForm<TFormModel> extends DestroyableMixin(EmptyInstance) {
    private _onFormChanged = new Subject<TFormModel>();
    private _onFormSubmitted = new Subject<TFormModel>();

    protected constructor(
        protected formInstance: FormGroup,
        protected initialFormState: TFormModel
    ) {
        super();

        // NOTE: sync form state & external params update on submit (e.g. search by text should reset page number, click on the page number should submit the form with the latest submitted search text, not current, etc.)
        this.onFormSubmitted
            .pipe(takeUntil(this.onDestroy))
            .subscribe(model => {
                this.formInstance.patchValue(model, { emitEvent: false });
                this.initialFormState = model;
            });
    }

    public get onFormChanged(): Observable<TFormModel> {
        return this._onFormChanged.pipe(
            distinctUntilChanged()
        );
    }

    public get onFormSubmitted(): Observable<TFormModel> {
        return this._onFormSubmitted.asObservable();
    }

    public getValue(): TFormModel {
        return this.getFormModel();
    }

    public getFormControl(alias: string): AbstractControl {
        return this.formInstance.get(alias);
    }

    public submit(partialFormModel: object = {}): void {
        if (this.isFormValid()) {
            this._onFormSubmitted.next(this.getFormModel(partialFormModel));
        }
    }

    protected getFormModel(partialFormModel: object = {}): TFormModel {
        const currentFormModel = _cloneDeep(this.formInstance.value) as TFormModel;
        return _cloneDeep({ ...currentFormModel, ...partialFormModel });
    }

    protected isFormValid(): boolean {
        return this.formInstance.valid;
    }
}
